import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

export const ImageUpload = ({
  value,
  onChange,
  aspectRatio = '16:9',
  label,
  className,
}) => {
  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles[0]) {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = (e) => {
          onChange(e.target.result);
        };
        reader.readAsDataURL(file);
      }
    },
    [onChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.gif']
    },
    maxFiles: 1,
  });

  const aspectRatioClass = {
    '16:9': 'aspect-video',
    '1:1': 'aspect-square',
    '4:3': 'aspect-[4/3]',
  }[aspectRatio];

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <label className="text-sm font-medium text-slate-700">{label}</label>
      )}
      <div
        {...getRootProps()}
        className={cn(
          'relative border-2 border-dashed rounded-lg overflow-hidden transition-colors',
          aspectRatioClass,
          isDragActive
            ? 'border-primary bg-primary/5'
            : 'border-slate-300 hover:border-slate-400',
          !value && 'cursor-pointer'
        )}
      >
        <input {...getInputProps()} />
        {value ? (
          <div className="relative w-full h-full">
            <img
              src={value}
              alt="Upload preview"
              className="w-full h-full object-cover"
            />
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2 h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex h-full flex-col items-center justify-center p-6 text-center">
            <Upload className="h-10 w-10 text-slate-400 mb-2" />
            <p className="text-sm font-medium text-slate-700">
              {isDragActive ? 'Drop image here' : 'Click or drag image to upload'}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              PNG, JPG, WEBP up to 10MB
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
